/**
 * 平台内容统一管理 API（对齐 PRD PAGE-PLT-OPS-001）
 * GET    /admin/api/v1/ops/contents
 * POST   /admin/api/v1/ops/contents
 * PUT    /admin/api/v1/ops/contents/{contentId}
 * POST   /admin/api/v1/ops/contents/{contentId}/publish
 * POST   /admin/api/v1/ops/contents/{contentId}/offline
 * DELETE /admin/api/v1/ops/contents/{contentId}
 */
import { adminGet, adminPost, adminPut, USE_MOCK } from './client';

export type ContentType = 'BANNER' | 'NOTICE' | 'FAQ' | 'POPUP';
export type ContentStatus = 'DRAFT' | 'PUBLISHED' | 'OFFLINE';

export interface ContentItem {
  contentId: string;
  type: ContentType;
  title: string;
  body: string;
  status: ContentStatus;
  updatedAt: string;
  locale: string;
  coverUrl?: string;
  sortOrder?: number;
  createdBy?: string;
}

type BeContentItem = {
  contentId?: number | string;
  title?: string;
  type?: string;
  body?: string;
  coverUrl?: string;
  sortOrder?: number;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

type BeList = {
  total?: number;
  page?: number;
  size?: number;
  items?: BeContentItem[];
};

let store: ContentItem[] = [
  {
    contentId: '1',
    type: 'BANNER',
    title: '开卡活动 Banner',
    body: '首页顶部轮播 · 新用户开卡减免',
    status: 'PUBLISHED',
    updatedAt: '2026-08-01 12:00',
    locale: 'zh-CN',
  },
  {
    contentId: '2',
    type: 'NOTICE',
    title: '系统维护公告',
    body: '8 月 5 日 02:00–04:00 窗口维护',
    status: 'DRAFT',
    updatedAt: '2026-08-02 09:30',
    locale: 'zh-CN',
  },
  {
    contentId: '3',
    type: 'FAQ',
    title: '如何充值？',
    body: '支持 USDT TRC20 与电汇…',
    status: 'PUBLISHED',
    updatedAt: '2026-07-20 16:00',
    locale: 'zh-CN',
  },
];

function mapItem(raw: BeContentItem): ContentItem {
  const rawStatus = (raw.status || 'DRAFT').toUpperCase();
  const status = (rawStatus === 'DELETED' ? 'OFFLINE' : rawStatus) as ContentStatus;
  return {
    contentId: String(raw.contentId ?? ''),
    type: ((raw.type || 'NOTICE').toUpperCase() as ContentType) || 'NOTICE',
    title: raw.title || '',
    body: raw.body || '',
    status,
    // 时间展示统一：保留原始 ISO，展示层用 formatDateTime
    updatedAt: raw.updatedAt || raw.createdAt || '',
    locale: 'zh-CN',
    coverUrl: raw.coverUrl,
    sortOrder: raw.sortOrder,
    createdBy: raw.createdBy,
  };
}

function toBe(item: ContentItem): BeContentItem {
  return {
    contentId: item.contentId,
    title: item.title,
    type: item.type,
    body: item.body,
    status: item.status,
    createdAt: item.updatedAt,
    createdBy: item.createdBy || 'mock',
  };
}

export function listContent(type?: ContentType | 'all', status?: ContentStatus | 'all') {
  return adminGet<BeList>('/ops/contents?page=1&size=100', () => ({
    total: store.length,
    page: 1,
    size: 100,
    items: store.map(toBe),
  })).then((res) => {
    if (!res.ok) return res;
    let items = (res.data.items ?? []).map(mapItem);
    if (type && type !== 'all') {
      items = items.filter((i) => i.type === type);
    }
    if (status && status !== 'all') {
      items = items.filter((i) => i.status === status);
    }
    return { ok: true as const, data: { items } };
  });
}

export function getContent(contentId: string) {
  return adminGet<BeContentItem>(`/ops/contents/${contentId}`, () => {
    const s = store.find((i) => i.contentId === contentId);
    if (!s) throw new Error('not found');
    return toBe(s);
  }).then((res) => {
    if (!res.ok) return res;
    return { ok: true as const, data: mapItem(res.data) };
  });
}

export function createContent(input: {
  type: ContentType;
  title: string;
  body: string;
  locale: string;
}) {
  return adminPost<number | { item: ContentItem }>(
    '/ops/contents',
    {
      title: input.title,
      type: input.type,
      body: input.body,
      coverUrl: '',
      sortOrder: 0,
    },
    () => {
      const item: ContentItem = {
        contentId: String(Date.now()),
        type: input.type,
        title: input.title,
        body: input.body,
        status: 'DRAFT',
        updatedAt: new Date().toISOString(),
        locale: input.locale,
      };
      store = [item, ...store];
      return { item };
    },
  ).then((res) => {
    if (!res.ok) return res;
    if (typeof res.data === 'number' || typeof res.data === 'string') {
      const contentId = String(res.data);
      return {
        ok: true as const,
        data: {
          item: {
            contentId,
            type: input.type,
            title: input.title,
            body: input.body,
            status: 'DRAFT' as const,
            updatedAt: new Date().toISOString(),
            locale: input.locale,
          },
        },
      };
    }
    return { ok: true as const, data: res.data as { item: ContentItem } };
  });
}

export function updateContent(
  contentId: string,
  patch: Partial<Pick<ContentItem, 'title' | 'body' | 'status' | 'type'>>,
) {
  // 状态变更走独立发布/下线端点
  if (patch.status === 'PUBLISHED') {
    return publishContent(contentId);
  }
  if (patch.status === 'OFFLINE') {
    return offlineContent(contentId);
  }

  return adminPut<unknown>(
    `/ops/contents/${contentId}`,
    {
      title: patch.title ?? '',
      type: patch.type,
      body: patch.body,
      coverUrl: '',
      sortOrder: 0,
    },
    () => {
      store = store.map((i) =>
        i.contentId === contentId
          ? {
              ...i,
              ...patch,
              updatedAt: new Date().toISOString(),
            }
          : i,
      );
      return {};
    },
  ).then((res) => {
    if (!res.ok) return res;
    const item =
      store.find((i) => i.contentId === contentId) ||
      ({
        contentId,
        type: (patch.type || 'NOTICE') as ContentType,
        title: patch.title || '',
        body: patch.body || '',
        status: (patch.status || 'DRAFT') as ContentStatus,
        updatedAt: '',
        locale: 'zh-CN',
      } satisfies ContentItem);
    return { ok: true as const, data: { item: { ...item, ...patch } } };
  });
}

/** 发布（DRAFT → PUBLISHED，二次确认前置） */
function publishContent(contentId: string) {
  return adminPost<unknown>(`/ops/contents/${contentId}/publish`, {}, () => {
    store = store.map((i) =>
      i.contentId === contentId
        ? { ...i, status: 'PUBLISHED' as const, updatedAt: new Date().toISOString() }
        : i,
    );
    return {};
  }).then((res) => {
    if (!res.ok) return res;
    const item = store.find((i) => i.contentId === contentId);
    return {
      ok: true as const,
      data: {
        item: item ?? ({
          contentId,
          type: 'NOTICE',
          title: '',
          body: '',
          status: 'PUBLISHED',
          updatedAt: '',
          locale: 'zh-CN',
        } satisfies ContentItem),
      },
    };
  });
}

/** 下线（PUBLISHED → OFFLINE） */
function offlineContent(contentId: string) {
  return adminPost<unknown>(`/ops/contents/${contentId}/offline`, {}, () => {
    store = store.map((i) =>
      i.contentId === contentId
        ? { ...i, status: 'OFFLINE' as const, updatedAt: new Date().toISOString() }
        : i,
    );
    return {};
  }).then((res) => {
    if (!res.ok) return res;
    const item = store.find((i) => i.contentId === contentId);
    return {
      ok: true as const,
      data: {
        item: item ?? ({
          contentId,
          type: 'NOTICE',
          title: '',
          body: '',
          status: 'OFFLINE',
          updatedAt: '',
          locale: 'zh-CN',
        } satisfies ContentItem),
      },
    };
  });
}

export { USE_MOCK };
